// src/components/library/LibraryPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getFitnessLibraryTasks,
  getLekhiLibraryPlanItems,
  getLekhiTopicContent,
  getLekhiTopicNumber,
  LEKHI_TOPICS,
  LEKHI_LEVELS
} from '../../services/firebase/firestoreService';
import { getSubjectName, getTopicNameMr } from '../../services/lekhiTopicNames';
import AudioView from '../lekhi/AudioView';
import TextView from '../lekhi/TextView';
import './LibraryPage.css';

const VideoModal = ({ url, onClose }) => {
  const getYouTubeId = (u) => {
    const match = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([^&\n?#/]+)/);
    return match ? match[1] : '';
  };
  return (
    <div className="video-overlay" onClick={onClose}>
      <div className="video-modal" onClick={e => e.stopPropagation()}>
        <button className="video-close" onClick={onClose}>✕</button>
        <div className="video-wrapper">
          <iframe src={`https://www.youtube.com/embed/${getYouTubeId(url)}`}
            title="Exercise Video" frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen/>
        </div>
      </div>
    </div>
  );
};

const FITNESS_FILTERS = [
  { id: 'all', mr: 'सर्व', en: 'All' },
  { id: 'athletics', mr: 'व्यायाम', en: 'Athletics' },
  { id: 'strength', mr: 'ताकद', en: 'Strength' },
  { id: 'nutrition', mr: 'आहार', en: 'Nutrition' }
];

// Subject names come from lekhiTopicNames.js (single source for the whole app)
const LEKHI_SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];
const LEKHI_SUBJECT_FILTERS = ['all', ...LEKHI_SUBJECTS];
const ALL_LABEL = { mr: 'सर्व', en: 'All' };

const LEVEL_LABELS = {
  mr: { easy: 'प्रारंभ', medium: 'प्रगत', hard: 'प्रावीण्य' },
  en: { easy: 'Easy', medium: 'Medium', hard: 'Hard' }
};

// Topics of the new plan, in plan order (current affairs not included)
const planTopicsFor = (subject) =>
  (LEKHI_TOPICS[subject] || []).filter(t => !t.startsWith('current_affairs'));

const TYPE_LABELS = {
  strength: { mr: 'ताकद', en: 'Strength' },
  athletics: { mr: 'व्यायाम', en: 'Athletics' },
  nutrition: { mr: 'आहार', en: 'Nutrition' }
};

const TEXT = {
  title: { mr: 'लायब्ररी', en: 'Library' },
  tabFitness: { mr: 'व्यायाम', en: 'Fitness' },
  tabLekhi: { mr: 'लेखी', en: 'Lekhi' },
  searchFitness: { mr: 'व्यायाम शोधा...', en: 'Search workouts...' },
  searchLekhi: { mr: 'विषय शोधा...', en: 'Search topics...' },
  loading: { mr: 'लोड होत आहे...', en: 'Loading...' },
  emptyFitness: { mr: 'व्हिडिओ उपलब्ध नाहीत.', en: 'No videos available.' },
  emptyLekhi: { mr: 'लेखी पूर्ण करा आणि इथे साहित्य मिळेल.', en: 'Complete Lekhi to see content here.' },
  noResults: { mr: 'काही सापडले नाही.', en: 'Nothing found.' },
  audio1: { mr: 'शिकवणी', en: 'Teaching' },
  audio2: { mr: 'चुका टाळा', en: 'Mistakes Drill' },
  studyText: { mr: 'पाठ्यपुस्तक', en: 'Study text' },
  revisionText: { mr: 'उजळणी पाठ', en: 'Revision text' },
  cheatSheet: { mr: 'झटपट उजळणी', en: 'Cheat sheet' },
  contentError: { mr: 'साहित्य लोड झाले नाही. पुन्हा प्रयत्न करा.', en: 'Could not load. Tap to try again.' }
};

const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

export default function LibraryPage({ setHideBottomNav }) {
  const { currentUser } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const t = (key) => TEXT[key][language] || TEXT[key].mr;
  const levelLabel = (l) => (LEVEL_LABELS[language] || LEVEL_LABELS.mr)[l] || l;

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const [activeTab, setActiveTab] = useState('fitness');
  const [fitnessTasks, setFitnessTasks] = useState([]);
  const [loadingFitness, setLoadingFitness] = useState(true);

  const [fitnessSearch, setFitnessSearch] = useState('');
  const [fitnessFilter, setFitnessFilter] = useState('all');

  // Lekhi (new plan)
  const [lekhiItems, setLekhiItems] = useState({ grammar: {}, math: {}, reasoning: {}, gk: {} });
  const [loadingLekhi, setLoadingLekhi] = useState(true);
  const [lekhiSearch, setLekhiSearch] = useState('');
  const [lekhiFilter, setLekhiFilter] = useState('all');
  const [expandedKey, setExpandedKey] = useState(null);       // 'subject|topic'
  const [selectedLevels, setSelectedLevels] = useState({});    // { 'subject|topic': level }
  const [contentCache, setContentCache] = useState({});        // { 'subject|topic|level': doc | 'loading' | 'error' }

  const [playingAudio, setPlayingAudio] = useState(null);
  const [libraryVideoUrl, setLibraryVideoUrl] = useState(null);
  const [readingText, setReadingText] = useState(null);

  useEffect(() => {
    const loadFitness = async () => {
      try {
        const data = await getFitnessLibraryTasks();
        setFitnessTasks(data);
      } catch (e) {} finally {
        setLoadingFitness(false);
      }
    };
    loadFitness();
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const loadLekhi = async () => {
      try {
        const data = await getLekhiLibraryPlanItems(currentUser.uid);
        setLekhiItems(data);
      } catch (e) {} finally {
        setLoadingLekhi(false);
      }
    };
    loadLekhi();
  }, [currentUser]);

  useEffect(() => {
    if (setHideBottomNav) {
      setHideBottomNav(playingAudio !== null || readingText !== null);
    }
    return () => {
      if (setHideBottomNav) setHideBottomNav(false);
    };
  }, [playingAudio, readingText, setHideBottomNav]);

  const handleFitnessTap = (task) => {
    if (task.youtubeURL) {
      setLibraryVideoUrl(task.youtubeURL);
    }
  };

  // ===== FITNESS BUCKET RENDER =====
  const getFilteredFitnessTasks = () => {
    let filtered = fitnessTasks;
    if (fitnessFilter !== 'all') {
      filtered = filtered.filter(t => t.type === fitnessFilter);
    }
    if (fitnessSearch.trim()) {
      const q = fitnessSearch.trim().toLowerCase();
      filtered = filtered.filter(t => {
        const nameMr = (t.name?.mr || '').toLowerCase();
        const nameEn = (t.name?.en || '').toLowerCase();
        const desc = (t.description || '').toLowerCase();
        return nameMr.includes(q) || nameEn.includes(q) || desc.includes(q);
      });
    }
    return filtered;
  };

  const renderFitnessTab = () => {
    if (loadingFitness) {
      return <div className="lib-status">{t('loading')}</div>;
    }
    const filtered = getFilteredFitnessTasks();
    if (fitnessTasks.length === 0) {
      return <div className="lib-status">{t('emptyFitness')}</div>;
    }
    return (
      <>
        <input
          type="text"
          className="lib-search"
          placeholder={t('searchFitness')}
          value={fitnessSearch}
          onChange={e => setFitnessSearch(e.target.value)}
        />
        <div className="lib-chip-row">
          {FITNESS_FILTERS.map(f => (
            <button
              key={f.id}
              className={`lib-chip lib-chip-fitness ${fitnessFilter === f.id ? 'active' : ''}`}
              onClick={() => setFitnessFilter(f.id)}
            >
              {f[language] || f.mr}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="lib-status">{t('noResults')}</div>
        ) : (
          <div className="lib-fitness-list">
            {filtered.map(task => {
              const thumb = getYouTubeThumbnail(task.youtubeURL);
              const typeLabel = TYPE_LABELS[task.type]?.[language] || task.type;
              return (
                <div key={task.id} className="lib-fitness-card" onClick={() => handleFitnessTap(task)}>
                  <div className="lib-fitness-thumb">
                    {thumb ? (
                      <img src={thumb} alt={task.name} />
                    ) : (
                      <div className="lib-fitness-thumb-placeholder">▶</div>
                    )}
                  </div>
                  <div className="lib-fitness-info">
                    <div className="lib-fitness-name">
                      {task.name?.[language] || task.name?.mr || task.name?.en || ''}
                    </div>
                    {task.description && (
                      <div className="lib-fitness-desc">{task.description}</div>
                    )}
                    <div className="lib-fitness-type">{typeLabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // ===== LEKHI (new plan) =====
  // Card per topic the student completed (any level), in plan order.
  const buildLekhiCards = () => {
    const subjects = lekhiFilter === 'all' ? LEKHI_SUBJECTS : [lekhiFilter];
    const q = lekhiSearch.trim().toLowerCase();
    const groups = [];
    subjects.forEach(subject => {
      const cards = [];
      planTopicsFor(subject).forEach(topic => {
        const levelMap = lekhiItems[subject]?.[topic];
        if (!levelMap) return;
        const levels = LEKHI_LEVELS.filter(l => levelMap[l] && (levelMap[l].study || levelMap[l].revision));
        if (levels.length === 0) return;
        const name = getTopicNameMr(topic);
        if (q && !name.toLowerCase().includes(q)) return;
        const latest = levels.reduce((best, l) =>
          (levelMap[l].last || '') > (levelMap[best].last || '') ? l : best, levels[0]);
        cards.push({ subject, topic, name, levels, latest, levelMap });
      });
      if (cards.length > 0) groups.push({ subject, cards });
    });
    return groups;
  };

  const loadContent = async (subject, topic, level) => {
    const k = `${subject}|${topic}|${level}`;
    const cached = contentCache[k];
    if (cached && cached !== 'error') return;
    setContentCache(prev => ({ ...prev, [k]: 'loading' }));
    try {
      const data = await getLekhiTopicContent(subject, topic, level);
      setContentCache(prev => ({ ...prev, [k]: data || 'error' }));
    } catch (e) {
      setContentCache(prev => ({ ...prev, [k]: 'error' }));
    }
  };

  const toggleCard = (card) => {
    const key = `${card.subject}|${card.topic}`;
    if (expandedKey === key) { setExpandedKey(null); return; }
    setExpandedKey(key);
    const level = selectedLevels[key] || card.latest;
    if (!selectedLevels[key]) setSelectedLevels(prev => ({ ...prev, [key]: level }));
    loadContent(card.subject, card.topic, level);
  };

  const selectLevel = (card, level) => {
    const key = `${card.subject}|${card.topic}`;
    setSelectedLevels(prev => ({ ...prev, [key]: level }));
    loadContent(card.subject, card.topic, level);
  };

  const renderTiles = (card, level) => {
    const k = `${card.subject}|${card.topic}|${level}`;
    const content = contentCache[k];
    if (!content || content === 'loading') {
      return <div className="lkl-msg">{t('loading')}</div>;
    }
    if (content === 'error') {
      return (
        <button className="lkl-msg lkl-retry" onClick={() => loadContent(card.subject, card.topic, level)}>
          {t('contentError')}
        </button>
      );
    }
    const done = card.levelMap[level] || {};
    const tiles = [];
    if (done.study) {
      if (content.audio1URL) tiles.push({ id: 'audio1', kind: 'audio', icon: '/icon-audio.svg', label: t('audio1'), url: content.audio1URL });
      if (content.audio2URL) tiles.push({ id: 'audio2', kind: 'audio', icon: '/icon-audio.svg', label: t('audio2'), url: content.audio2URL });
      if (content.studyText) tiles.push({ id: 'studyText', kind: 'text', icon: '/icon-study.svg', label: t('studyText'), text: content.studyText });
    }
    if (done.revision) {
      if (content.revisionText) tiles.push({ id: 'revisionText', kind: 'text', icon: '/icon-revision.svg', label: t('revisionText'), text: content.revisionText });
      if (content.cheatSheet) tiles.push({ id: 'cheatSheet', kind: 'text', icon: '/icon-cheatsheet.svg', label: t('cheatSheet'), text: content.cheatSheet });
    }
    return (
      <div className="lkl-grid">
        {tiles.map(tile => (
          <button
            key={tile.id}
            className={`lkl-tile ${tile.kind === 'audio' ? 'audio' : ''}`}
            onClick={() => {
              if (tile.kind === 'audio') {
                setPlayingAudio({ audio: { audioURL: tile.url }, label: tile.label, topic: card.name });
              } else {
                setReadingText({ text: tile.text });
              }
            }}
          >
            <img src={tile.icon} alt="" className="lkl-tile-icon" />
            <span className="lkl-tile-label">{tile.label}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderLekhiTab = () => {
    if (loadingLekhi) {
      return <div className="lib-status">{t('loading')}</div>;
    }
    const hasAnyContent = LEKHI_SUBJECTS.some(s => Object.keys(lekhiItems[s] || {}).length > 0);
    if (!hasAnyContent) {
      return <div className="lib-status">{t('emptyLekhi')}</div>;
    }
    const groups = buildLekhiCards();

    return (
      <>
        <input
          type="text"
          className="lib-search"
          placeholder={t('searchLekhi')}
          value={lekhiSearch}
          onChange={e => setLekhiSearch(e.target.value)}
        />
        <div className="lib-chip-row">
          {LEKHI_SUBJECT_FILTERS.map(id => (
            <button
              key={id}
              className={`lib-chip lib-chip-lekhi ${lekhiFilter === id ? 'active' : ''}`}
              onClick={() => setLekhiFilter(id)}
            >
              {id === 'all' ? (ALL_LABEL[language] || ALL_LABEL.mr) : getSubjectName(id, language)}
            </button>
          ))}
        </div>

        {groups.length === 0 ? (
          <div className="lib-status">{t('noResults')}</div>
        ) : (
          groups.map(group => (
            <div key={group.subject}>
              {lekhiFilter === 'all' && (
                <div className="lkl-group-title">{getSubjectName(group.subject, language)}</div>
              )}
              <div className="lkl-list">
                {group.cards.map(card => {
                  const key = `${card.subject}|${card.topic}`;
                  const open = expandedKey === key;
                  const level = selectedLevels[key] || card.latest;
                  return (
                    <div key={key} className={`lkl-card ${open ? 'open' : ''}`}>
                      <button className="lkl-row" onClick={() => toggleCard(card)}>
                        <span className="lkl-num">{getLekhiTopicNumber(card.subject, card.topic)}</span>
                        <span className="lkl-name">{card.name}</span>
                        <span className="lkl-tags">
                          {card.levels.map(l => <span key={l} className="lkl-tag">{levelLabel(l)}</span>)}
                        </span>
                        <span className="lkl-arrow">{open ? '▲' : '▼'}</span>
                      </button>
                      {open && (
                        <div className="lkl-body">
                          {card.levels.length > 1 && (
                            <div className="lkl-levels">
                              {card.levels.map(l => (
                                <button key={l}
                                  className={`lkl-level-btn ${l === level ? 'active' : ''}`}
                                  onClick={() => selectLevel(card, l)}>
                                  {levelLabel(l)}
                                </button>
                              ))}
                            </div>
                          )}
                          {renderTiles(card, level)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </>
    );
  };

  // ===== AUDIO / TEXT VIEWER =====
  if (playingAudio) {
    return (
      <AudioView
        audio={playingAudio.audio}
        blockLabel={playingAudio.label}
        topicName={playingAudio.topic}
        language={language}
        onComplete={() => setPlayingAudio(null)}
        onBack={() => setPlayingAudio(null)}
      />
    );
  }

  if (readingText) {
    return (
      <TextView
        reference
        textContent={readingText}
        language={language}
        onComplete={() => setReadingText(null)}
        onBack={() => setReadingText(null)}
      />
    );
  }

  // ===== MAIN LIBRARY UI =====
  return (
    <div className="lib-container">
      <div className="lib-header">
        <div className="lib-header-title">{t('title')}</div>
        <button className="lang-toggle" onClick={handleLanguageToggle}>
          {language === 'en' ? 'मराठी' : 'EN'}
        </button>
      </div>

      <div className="lib-tabs">
        <button
          className={`lib-tab lib-tab-fitness ${activeTab === 'fitness' ? 'active' : ''}`}
          onClick={() => setActiveTab('fitness')}
        >
          {t('tabFitness')}
        </button>
        <button
          className={`lib-tab lib-tab-lekhi ${activeTab === 'lekhi' ? 'active' : ''}`}
          onClick={() => setActiveTab('lekhi')}
        >
          {t('tabLekhi')}
        </button>
      </div>

      <div className="lib-content">
        {activeTab === 'fitness' ? renderFitnessTab() : renderLekhiTab()}
      </div>

      {libraryVideoUrl && (
        <VideoModal url={libraryVideoUrl} onClose={() => setLibraryVideoUrl(null)} />
      )}
    </div>
  );
}